import axios, { AxiosError } from 'axios';

/**
 * Company Service
 *
 * Handles all company-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Company endpoints
const COMPANY_ENDPOINTS = {
  list: `${API_BASE_URL}/companies`,
  me: `${API_BASE_URL}/companies/me`,
  byId: (id: string) => `${API_BASE_URL}/companies/${id}`,
  update: (id: string) => `${API_BASE_URL}/companies/${id}`,
  stats: (id: string) => `${API_BASE_URL}/companies/${id}/stats`,
  platformStats: `${API_BASE_URL}/companies/platform/stats`
};

/**
 * Company interface matching backend
 */
export interface Company {
  id: string;
  name: string;
  subdomain?: string | null;
  logo_url?: string | null;
  website?: string | null;
  about?: string | null;
  industry?: string | null;
  company_size?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  billing_email?: string | null;
  primary_contact_name?: string | null;
  primary_contact_phone?: string | null;
  primary_contact_email?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_phone?: string | null;
  secondary_contact_email?: string | null;
  billing_contact_name?: string | null;
  billing_contact_phone?: string | null;
  billing_contact_email?: string | null;
  subscription_tier?: 'free' | 'starter' | 'professional' | 'enterprise';
  subscription_status?: 'active' | 'trial' | 'suspended' | 'cancelled';
  trial_ends_at?: string | null;
  subscription_ends_at?: string | null;
  max_users?: number | null;
  max_projects?: number | null;
  created_at: string;
  updated_at?: string | null;
  is_active: boolean;
}

/**
 * Company creation input
 */
export interface CreateCompanyInput {
  name: string;
  subdomain?: string;
  logo_url?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
  billing_email?: string;
  subscription_tier?: 'free' | 'starter' | 'professional' | 'enterprise';
  subscription_status?: 'active' | 'trial' | 'suspended' | 'cancelled';
  trial_ends_at?: string;
  max_users?: number;
  max_projects?: number;
}

/**
 * Company update input
 */
export interface UpdateCompanyInput {
  name?: string;
  subdomain?: string | null;
  logo_url?: string | null;
  website?: string | null;
  about?: string | null;
  industry?: string | null;
  company_size?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  billing_email?: string | null;
  primary_contact_name?: string | null;
  primary_contact_phone?: string | null;
  primary_contact_email?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_phone?: string | null;
  secondary_contact_email?: string | null;
  billing_contact_name?: string | null;
  billing_contact_phone?: string | null;
  billing_contact_email?: string | null;
}

/**
 * Company statistics
 */
export interface CompanyStats {
  company_id: string;
  total_users: number;
  active_users: number;
  total_projects: number;
  active_projects: number;
  total_requirements: number;
}

/**
 * Platform-wide statistics (for Platform Admins)
 */
export interface PlatformStats {
  total_companies: number;
  total_users: number;
  total_projects: number;
  total_requirements: number;
}

/**
 * API Response interfaces
 */
export interface CompanyResponse {
  success: boolean;
  data: Company;
}

export interface ListCompaniesResponse {
  success: boolean;
  data: {
    companies: Company[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface CompanyStatsResponse {
  success: boolean;
  data: CompanyStats;
}

export interface PlatformStatsResponse {
  success: boolean;
  data: PlatformStats;
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Create a new company (Platform Admin only)
 */
export async function createCompany(
  data: CreateCompanyInput,
  token: string
): Promise<Company> {
  try {
    const response = await axios.post<CompanyResponse>(COMPANY_ENDPOINTS.list, data, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * List all companies (Platform Admin only)
 */
export async function listCompanies(
  params: {
    page?: number;
    limit?: number;
    subscription_status?: 'active' | 'trial' | 'suspended' | 'cancelled';
    subscription_tier?: 'free' | 'starter' | 'professional' | 'enterprise';
  },
  token: string
): Promise<ListCompaniesResponse['data']> {
  try {
    const response = await axios.get<ListCompaniesResponse>(COMPANY_ENDPOINTS.list, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get current user's company
 */
export async function getCurrentCompany(token: string): Promise<Company> {
  try {
    const response = await axios.get<CompanyResponse>(COMPANY_ENDPOINTS.me, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get company by ID
 */
export async function getCompanyById(id: string, token: string): Promise<Company> {
  try {
    const response = await axios.get<CompanyResponse>(COMPANY_ENDPOINTS.byId(id), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update company profile
 */
export async function updateCompany(
  id: string,
  data: UpdateCompanyInput,
  token: string
): Promise<Company> {
  try {
    const response = await axios.put<CompanyResponse>(
      COMPANY_ENDPOINTS.update(id),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get company statistics
 */
export async function getCompanyStats(id: string, token: string): Promise<CompanyStats> {
  try {
    const response = await axios.get<CompanyStatsResponse>(COMPANY_ENDPOINTS.stats(id), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get platform-wide statistics (Platform Admin only)
 */
export async function getPlatformStats(token: string): Promise<PlatformStats> {
  try {
    const response = await axios.get<PlatformStatsResponse>(COMPANY_ENDPOINTS.platformStats, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Check if subdomain is available
 */
export async function checkSubdomainAvailability(subdomain: string, token: string): Promise<{ available: boolean }> {
  try {
    const response = await axios.get<{ success: boolean; available: boolean }>(
      `${API_BASE_URL}/companies/check-subdomain`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { subdomain }
      }
    );
    return { available: response.data.available };
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  createCompany,
  listCompanies,
  getCurrentCompany,
  getCompanyById,
  updateCompany,
  getCompanyStats,
  checkSubdomainAvailability
};
